// Tanner Fry
// tfry@monetagroup.com
// File contains the header component for the Twilio Conversations dashboard.

import React, { useRef, useState } from "react";
import { capitalizeFirstLetter } from "../../components/UtilitiesTS";
import { IUserDetails } from "../../interfaces/General";
import { useAppSelectorTs, useAppDispatchTS } from "../../redux/hooksTS";
import { reduxSelectIsDarkMode } from "../../redux/slicesTS/UsersSlice";
import { reduxFilterPopper, reduxSetFilterPopper } from "../../redux/slicesTS/TwilioConversationsSlice";
import Button, { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import SearchAllConversationMessages from "./SearchAllConversationMessages";
import { ITwilioConversationsConversationProps } from "../../interfaces/TwilioConversations";
import TwilioNumberForwarding from "./TwilioNumberForwarding";
import TuneIcon from '@mui/icons-material/Tune';
import Popper from "@mui/material/Popper";
import IconButton from "@mui/material/IconButton";
interface ITwilioHeaderProps {
    chatType: string;
    advisor_or_staff_number: string;
    onFilterClick: (newText: string) => void;
    filterOption: string;
    searchAllConversationsText: string
    setSearchAllConversationsText: React.Dispatch<React.SetStateAction<string>>;
    userDetails: IUserDetails;
    handleConversationClicked: (conversation: ITwilioConversationsConversationProps) => void;
    messageSearchId: number;
    setMessageSearchId: React.Dispatch<React.SetStateAction<number>>;
    handlePullCurrentPageOfMessages: (conversation: ITwilioConversationsConversationProps) => void;
    setCurrentConversationMessagesPage: React.Dispatch<React.SetStateAction<number>>;
    currentConversationMessagesPage: number;
    setHasUserScrolled: React.Dispatch<React.SetStateAction<boolean>>;
    setMessagesHasNextPage: React.Dispatch<React.SetStateAction<boolean>>;
}

const ColorButton = styled(Button)<ButtonProps>(({ theme }) => ({
    color: '#fff',
    backgroundColor: '#7E8C9A',
    borderRadius: '20px',
    fontSize: '12px',
    lineHeight: '12px',
    padding: '8px 12px',
    fontWeight: 600,
    '&:hover': {
        backgroundColor: '#41BA77',
    },
    '&.active': {
        backgroundColor: '#41BA77',
    },
}));


const TwilioHeader = ({
    chatType,
    advisor_or_staff_number,
    filterOption,
    onFilterClick,
    searchAllConversationsText,
    setSearchAllConversationsText,
    userDetails,
    handleConversationClicked,
    messageSearchId,
    setMessageSearchId,
    handlePullCurrentPageOfMessages,
    setCurrentConversationMessagesPage,
    currentConversationMessagesPage,
    setHasUserScrolled,
    setMessagesHasNextPage
}: ITwilioHeaderProps) => {
    const isDarkMode = useAppSelectorTs(reduxSelectIsDarkMode);
    const filterPopperOpen = useAppSelectorTs(reduxFilterPopper);
    const dispatch = useAppDispatchTS();
    const filterPopperRef = useRef<HTMLDivElement>(null);
    return (
        <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
                <span className="head-icon me-16">
                    <em className="icon-chat-alt-2-filled"></em>
                </span>
                <h4 className="m-0" style={{ color: isDarkMode ? "#fff" : "" }}>{capitalizeFirstLetter(chatType)}</h4>
                <TwilioNumberForwarding
                    chatType={chatType}
                    advisor_or_staff_number={advisor_or_staff_number}
                    userDetails={userDetails}
                />
            </div>
            <div ref={filterPopperRef}>
                <IconButton
                    className="d-md-none"
                    onClick={() => { dispatch(reduxSetFilterPopper(!filterPopperOpen)) }}

                >
                    <TuneIcon />
                </IconButton>
            </div>
            <div className="conversation-filters d-none d-md-flex">
                <div className="d-flex align-items-center">
                    <ColorButton className={"btn-small-plain " + (filterOption === 'all' ? 'active' : '')} onClick={(event) => { onFilterClick("all"); }}>
                        ALL
                    </ColorButton>
                    <ColorButton className={"btn-small-plain mx-8 " + (filterOption === 'unread' ? 'active' : '')} onClick={(event) => { onFilterClick("unread"); }}>
                        UNREAD
                    </ColorButton>
                    <ColorButton className={"btn-small-plain me-8 " + (filterOption === 'archived' ? 'active' : '')} onClick={(event) => { onFilterClick("archived"); }}>
                        ARCHIVED
                    </ColorButton>
                </div>
                <SearchAllConversationMessages
                    setSearchAllConversationsText={setSearchAllConversationsText}
                    searchAllConversationsText={searchAllConversationsText}
                    userDetails={userDetails}
                    handleConversationClicked={handleConversationClicked}
                    messageSearchId={messageSearchId}
                    setMessageSearchId={setMessageSearchId}
                    handlePullCurrentPageOfMessages={handlePullCurrentPageOfMessages}
                    setCurrentConversationMessagesPage={setCurrentConversationMessagesPage}
                    setHasUserScrolled={setHasUserScrolled}
                    setMessagesHasNextPage={setMessagesHasNextPage}
                    chatType={chatType}
                />
            </div>
            <Popper
                open={filterPopperOpen}
                anchorEl={filterPopperRef.current}
                className="d-md-none message-filter-popper"
                placement="bottom-end"
            >
                <div className="conversation-filters">
                    <div className="d-flex align-items-center">
                        <ColorButton className={"btn-small-plain " + (filterOption === 'all' ? 'active' : '')} onClick={(event) => { onFilterClick("all"); }}>
                            ALL
                        </ColorButton>
                        <ColorButton className={"btn-small-plain mx-8 " + (filterOption === 'unread' ? 'active' : '')} onClick={(event) => { onFilterClick("unread"); }}>
                            UNREAD
                        </ColorButton>
                        <ColorButton className={"btn-small-plain me-8 " + (filterOption === 'archived' ? 'active' : '')} onClick={(event) => { onFilterClick("archived"); }}>
                            ARCHIVED
                        </ColorButton>
                    </div>
                    <SearchAllConversationMessages
                        setSearchAllConversationsText={setSearchAllConversationsText}
                        searchAllConversationsText={searchAllConversationsText}
                        userDetails={userDetails}
                        handleConversationClicked={handleConversationClicked}
                        messageSearchId={messageSearchId}
                        setMessageSearchId={setMessageSearchId}
                        handlePullCurrentPageOfMessages={handlePullCurrentPageOfMessages}
                        setCurrentConversationMessagesPage={setCurrentConversationMessagesPage}
                        setHasUserScrolled={setHasUserScrolled}
                        setMessagesHasNextPage={setMessagesHasNextPage}
                        chatType={chatType}
                    />
                </div>
            </Popper>
        </div>
    );
}

export default TwilioHeader;
